import { useMemo } from 'react';
import createDictionaryAccessor, { createTimeZoneAccessor } from '@/creditflowresearch/application/createDictionaryAccessor';
import type {
  DictionaryName,
  TDictionaryItem,
} from '@/creditflowresearch/application/Dictionaries/DictionaryRegistry';
import type { IDictionaryIndustry, IDictionaryTimeZone } from '@/creditflowresearch/domain/vo/IDictionary';
import type { ISector } from '@/creditflowresearch/domain/vo/ISector';

export type TDictionaryData = Partial<Record<DictionaryName, unknown[]>>;

type TIdentifiable = { id: string | number };
type TAccessor = ReturnType<typeof createDictionaryAccessor>;

type TTypedAccessor<T extends TIdentifiable> = ReturnType<typeof createDictionaryAccessor<T>>;

export type TDictionaryAccessors = { [K in DictionaryName]: TTypedAccessor<TDictionaryItem<K>> } & {
  timeZones: ReturnType<typeof createTimeZoneAccessor<IDictionaryTimeZone>>,
  subindustries: TAccessor,
  subsectors: TAccessor,
};

const useDictionaryAccessors = (data: TDictionaryData) => useMemo(() => {
  const accessors = Object.fromEntries(
    Object.entries(data).map(([name, list]) => [
      name,
      name === 'timeZones'
        ? createTimeZoneAccessor((list ?? []) as IDictionaryTimeZone[])
        : createDictionaryAccessor((list ?? []) as TIdentifiable[]),
    ]),
  );

  const industries = (data.industries ?? []) as IDictionaryIndustry[];
  const sectors = (data.sectors ?? []) as ISector[];

  return {
    ...accessors,
    subindustries: createDictionaryAccessor(industries.flatMap((industry) => industry.subindustries ?? [])),
    subsectors: createDictionaryAccessor(sectors.flatMap((sector) => sector.subsectors ?? [])),
  } as unknown as TDictionaryAccessors;
}, [data]);

export default useDictionaryAccessors;
